import type { TransferConfirmationViewProps } from './index.types';
import { useResolveTransferNumber } from 'Modules/softphone/hooks/useResolveTransferNumber';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React, { useCallback } from 'react';
import { TransferActions } from '../TransferActions';
import { TransferHeader } from '../TransferHeader';
import { TransferContactInfo } from './TransferContactInfo';
import { TransferPhoneSelector } from './TransferPhoneSelector';

/**
 * TransferConfirmationView - Confirmation screen for transfer destination.
 */
const TransferConfirmationView = ({
  onConfirm,
  testID,
}: TransferConfirmationViewProps): React.ReactElement => {
  const componentTestId = testID ? `${testID}-confirmation` : 'confirmation';

  useResolveTransferNumber();

  const {
    transferTarget,
    transferSelectedPhoneNumber: selectedPhoneNumber,
    resetTransferFlow,
    setTransferStep,
    setTransferTarget,
    setTransferSelectedPhoneNumber,
  } = useSoftphoneStore((state) => ({
    transferTarget: state.transferTarget,
    transferSelectedPhoneNumber: state.transferSelectedPhoneNumber,
    resetTransferFlow: state.resetTransferFlow,
    setTransferStep: state.setTransferStep,
    setTransferTarget: state.setTransferTarget,
    setTransferSelectedPhoneNumber: state.setTransferSelectedPhoneNumber,
  }));

  const handleBack = useCallback(() => {
    resetTransferFlow();
  }, [resetTransferFlow]);

  const handleCancel = useCallback(() => {
    // Go back to search step preserving the search term
    setTransferStep('search');
    setTransferTarget(null);
    setTransferSelectedPhoneNumber(null);
  }, [setTransferStep, setTransferTarget, setTransferSelectedPhoneNumber]);

  const handleTransfer = useCallback(() => {
    if (transferTarget && selectedPhoneNumber) {
      onConfirm(transferTarget, selectedPhoneNumber);
    }
  }, [onConfirm, transferTarget, selectedPhoneNumber]);

  return (
    <div
      className="flex flex-col h-full min-h-0 gap-[16px] overflow-y-auto scroll-area-content"
      data-testid={componentTestId}
      role="region"
      aria-label="Transfer confirmation"
    >
      <TransferHeader onBack={handleBack} testID={componentTestId} />

      <div
        className="mx-[16px] flex flex-col gap-[12px]"
        data-testid={`${componentTestId}-contact-section`}
      >
        <TransferContactInfo onCancel={handleCancel} testID={componentTestId} />

        <TransferPhoneSelector testID={componentTestId} />
      </div>

      <TransferActions onTransfer={handleTransfer} testID={componentTestId} />
    </div>
  );
};

TransferConfirmationView.displayName = 'TransferConfirmationView';

export { TransferConfirmationView };
